import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { type DismissKey, shouldShowOAuthBanner } from '../../ui/oauth-banner/utils';

export type SlackAuthUpgradeBanner = {
	outboundAuthUrl: string;
	extraAttributes: {
		isUpgrade: true;
		serviceKey: string;
		containerId: string | undefined;
	};
};

const SLACK_CONNECTOR_TYPE = 'slack-connector';
const ENTERPRISE_GRID_SERVICE_KEY_PREFIX = 'enterprise-grid-search-';

/**
 * Counted separately from `slack` so dismissing the  Slack connect banner
 * does not also suppress authorization-upgrade prompt.
 */
export const SLACK_AUTH_UPGRADE_DISMISS_KEY: DismissKey = 'slack.upgrade';

const ENFORCE_MAX_DISMISSALS = true;

export const shouldShowSlackAuthUpgradeBanner = (): boolean =>
	shouldShowOAuthBanner(SLACK_AUTH_UPGRADE_DISMISS_KEY, ENFORCE_MAX_DISMISSALS);

/**
 * Returns Slack authorization-upgrade banner data only for the full Slack
 * datasource that CDS explicitly marks as upgradeable. The returned URL is
 * deliberately the unmodified backend value; URL parsing is used solely to
 * validate it and confirm the Enterprise Grid upgrade service key.
 */
export const getSlackAuthUpgradeBanner = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): SlackAuthUpgradeBanner | undefined => {
	const slackDatasources = thirdPartyConfigs.slack?.datasources ?? [];
	const datasource = slackDatasources.find(({ type }) => type === SLACK_CONNECTOR_TYPE);

	// A datasource only qualifies for the auth-upgrade banner when the backend
	// says an upgrade is required (hasAuthUpgrade) and the user does not
	// otherwise need normal OAuth (userNeedsOAuth === false, i.e. isUserOAuthed).
	// This prevents showing the "reconnect" banner to users who were never
	// connected in the first place, since backend can set hasAuthUpgrade=true
	// even when there is no existing account.
	if (
		datasource?.hasAuthUpgrade !== true ||
		datasource.userNeedsOAuth ||
		!datasource.outboundAuthUrl
	) {
		return undefined;
	}

	try {
		const url = new URL(datasource.outboundAuthUrl);
		const serviceKey = url.searchParams.get('serviceKey');
		if (!serviceKey?.startsWith(ENTERPRISE_GRID_SERVICE_KEY_PREFIX)) {
			return undefined;
		}

		return {
			outboundAuthUrl: datasource.outboundAuthUrl,
			extraAttributes: {
				isUpgrade: true,
				serviceKey,
				containerId: url.searchParams.get('containerId') ?? undefined,
			},
		};
	} catch {
		return undefined;
	}
};
